import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule } from '@angular/router';
import { User } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { TranslatePipe } from '../../i18n/translate.pipe';
import { I18nService } from '../../i18n/i18n.service';

/** Hasła w obu polach muszą być takie same */
export const passwordsMatch = (group: AbstractControl): ValidationErrors | null =>
  group.get('password')?.value === group.get('passwordRepeat')?.value ? null : { passwordMismatch: true };

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule,
              MatFormFieldModule, MatInputModule, RouterModule, TranslatePipe]
})
export class SignupComponent implements OnInit {
  emailFormControl = new UntypedFormControl('', [Validators.required, Validators.email]);
  usernameFormControl = new UntypedFormControl('', [Validators.required]);
  nameFormControl = new UntypedFormControl('', [Validators.required]);
  surnameFormControl = new UntypedFormControl('', [Validators.required]);
  passwordFormControl = new UntypedFormControl('', [Validators.required, Validators.minLength(6)]);
  passwordRepeatFormControl = new UntypedFormControl('', [Validators.required]);
  /** Zgody wymagane do zalozenia konta - bez nich formularz jest nieprawidlowy. */
  termsFormControl = new UntypedFormControl(false, [Validators.requiredTrue]);
  privacyFormControl = new UntypedFormControl(false, [Validators.requiredTrue]);

  signupForm = new UntypedFormGroup({
    email: this.emailFormControl,
    username: this.usernameFormControl,
    name: this.nameFormControl,
    surname: this.surnameFormControl,
    password: this.passwordFormControl,
    passwordRepeat: this.passwordRepeatFormControl,
    terms: this.termsFormControl,
    privacy: this.privacyFormControl
  }, { validators: passwordsMatch });

  submitting = false;
  /** Po udanej rejestracji pokazujemy prośbę o potwierdzenie adresu */
  registered = false;
  facebookAvailable = false;

  constructor(public dialogRef: MatDialogRef<SignupComponent>,
              private authService: AuthService,
              private i18n: I18nService) { }

  ngOnInit() {
    this.authService.getAuthProviders().subscribe({
      next: (providers) => this.facebookAvailable = !!providers?.facebook,
      error: () => this.facebookAvailable = false
    });
  }

  onRegisterLocal() {
    if (this.signupForm.invalid || this.submitting) {
      return;
    }

    const user = new User();
    user.email = (this.emailFormControl.value as string).trim();
    user.username = (this.usernameFormControl.value as string).trim();
    user.legal_name = this.nameFormControl.value;
    user.legal_surname = this.surnameFormControl.value;
    user.password = this.passwordFormControl.value;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    user.terms_accepted = this.termsFormControl.value === true;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    user.privacy_accepted = this.privacyFormControl.value === true;
    // język, w którym ktoś właśnie zakłada konto - w nim przyjdzie mail
    // z potwierdzeniem i w nim zapisujemy preferencję na koncie
    user.language = this.i18n.lang;

    this.submitting = true;
    this.authService.signupLocal(user).subscribe({
      next: (data) => {
        this.submitting = false;
        // Błąd biznesowy wraca jako sama koperta ze statusem - komunikat pokazuje interceptor
        if (data) {
          this.registered = true;
        }
      },
      error: () => this.submitting = false
    });
  }

  onCancelClick(): void {
    this.dialogRef.close(this.registered);
  }

  /** Zgody obowiazuja tak samo przy zakladaniu konta przez Facebooka. */
  get consentsGiven() {
    return this.termsFormControl.value === true && this.privacyFormControl.value === true;
  }

  onAuthenticateFacebook() {
    if (!this.consentsGiven) {
      return;
    }
    this.authService.signinFacebook();
  }

}
